import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { DeliveryRoute } from '../../../_models/delivery_route';
import { DeliveryRouteService } from '../../delivery-route.service';

@Component({
  selector: 'app-delivery-route-list',
  templateUrl: './delivery-route-list.component.html',
  styleUrls: ['./delivery-route-list.component.sass']
})
export class DeliveryRouteListComponent implements OnInit {
  readonly pageSize = 15;

  deliveryRouteList: DeliveryRoute[] = [];
  selectedRoute: DeliveryRoute = null;
  searchForm: FormGroup;
  routeForm: FormGroup;
  pagination: any = { count: 0, page: 1, page_size: 15, total_pages: 1 };
  loading = false;
  saving = false;
  deleteModal = false;
  feedbackMessage = '';
  errorMessage = '';

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    private deliveryRouteService: DeliveryRouteService
  ) {}

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      f_country_code: [''],
      f_search: [''],
      f_active: ['true'],
      f_page: [1],
      f_page_size: [this.pageSize]
    });
    this.routeForm = this.formBuilder.group({
      id: [null],
      route_number: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      name: ['', [Validators.required, Validators.maxLength(120)]],
      country_code: ['ES', [Validators.required, Validators.pattern(/^[A-Za-z]{2}$/)]],
      country_name: ['España', [Validators.required, Validators.maxLength(80)]],
      active: [true],
      notes: ['']
    });
    this.loadRoutes();
  }

  searchRoutes(): void {
    this.searchForm.controls.f_page.setValue(1);
    this.loadRoutes();
  }

  loadRoutes(): void {
    this.loading = true;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.deliveryRouteService.getDeliveryRoutes(this.searchForm.value).subscribe(data => {
      const payload = data || {};
      this.deliveryRouteList = Array.isArray(payload) ? payload : (payload.rows || []);
      this.pagination = Array.isArray(payload)
        ? { count: this.deliveryRouteList.length, page: 1, page_size: this.pageSize, total_pages: 1 }
        : {
            count: Number(payload.count || 0),
            page: Number(payload.page || 1),
            page_size: Number(payload.page_size || this.pageSize),
            total_pages: Number(payload.total_pages || 1)
          };
      this.loading = false;
    });
  }

  clearFilters(): void {
    this.searchForm.patchValue({
      f_country_code: '',
      f_search: '',
      f_active: 'true',
      f_page: 1,
      f_page_size: this.pageSize
    });
    this.loadRoutes();
  }

  createNew(): void {
    this.selectedRoute = null;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.routeForm.reset({
      id: null,
      route_number: null,
      name: '',
      country_code: 'ES',
      country_name: 'España',
      active: true,
      notes: ''
    });
  }

  editRoute(routeIn: DeliveryRoute): void {
    this.selectedRoute = routeIn;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.routeForm.patchValue({
      id: routeIn.id,
      route_number: routeIn.route_number,
      name: routeIn.name,
      country_code: routeIn.country_code || 'ES',
      country_name: routeIn.country_name || 'España',
      active: routeIn.active === true,
      notes: routeIn.notes || ''
    });
  }

  saveRoute(): void {
    if (this.routeForm.invalid || this.saving) {
      this.errorMessage = 'Revisa el número de ruta, el nombre y el país.';
      return;
    }
    const payload = this.normalizeFormPayload();
    this.saving = true;
    this.feedbackMessage = '';
    this.errorMessage = '';
    const request = payload.id
      ? this.deliveryRouteService.updateDeliveryRoute(payload)
      : this.deliveryRouteService.createDeliveryRoute(payload);
    request.subscribe(data => {
      this.saving = false;
      if (!data) {
        this.errorMessage = 'No se ha podido guardar la ruta. Comprueba que el número no esté repetido.';
        return;
      }
      this.feedbackMessage = 'Ruta de entrega guardada correctamente.';
      this.editRoute(data);
      this.loadRoutes();
    });
  }

  askDelete(routeIn: DeliveryRoute): void {
    if (Number(routeIn && routeIn.customer_count || 0) > 0) {
      this.errorMessage = 'La ruta está asignada a clientes. Desactívala si ya no debe utilizarse.';
      return;
    }
    this.selectedRoute = routeIn;
    this.deleteModal = true;
  }

  confirmDelete(): void {
    if (!this.selectedRoute) return;
    this.deliveryRouteService.deleteDeliveryRoute(this.selectedRoute).subscribe(data => {
      this.deleteModal = false;
      if (data === null || data === undefined) {
        this.errorMessage = 'No se ha podido eliminar la ruta. Puede estar asignada a algún cliente.';
        return;
      }
      this.createNew();
      this.loadRoutes();
    });
  }

  goToPage(pageIn: number): void {
    const targetPage = Math.min(this.getTotalPages(), Math.max(1, Number(pageIn || 1)));
    this.searchForm.controls.f_page.setValue(targetPage);
    this.loadRoutes();
  }

  getCurrentPage(): number {
    return Number(this.pagination && this.pagination.page ? this.pagination.page : 1);
  }

  getTotalPages(): number {
    return Math.max(1, Number(this.pagination && this.pagination.total_pages ? this.pagination.total_pages : 1));
  }

  getTotalCount(): number {
    return Number(this.pagination && this.pagination.count ? this.pagination.count : 0);
  }

  getPageStart(): number {
    return this.deliveryRouteList.length > 0 ? ((this.getCurrentPage() - 1) * this.pageSize) + 1 : 0;
  }

  getPageEnd(): number {
    return this.deliveryRouteList.length > 0
      ? Math.min(this.getTotalCount(), this.getPageStart() + this.deliveryRouteList.length - 1)
      : 0;
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event && event.key === 'Enter') {
      event.preventDefault();
      this.searchRoutes();
    }
  }

  private normalizeFormPayload(): DeliveryRoute {
    const raw = this.routeForm.value;
    return {
      id: raw.id ? Number(raw.id) : null,
      route_number: Number(raw.route_number),
      name: (raw.name || '').toString().trim(),
      country_code: (raw.country_code || '').toString().trim().toUpperCase(),
      country_name: (raw.country_name || '').toString().trim(),
      active: raw.active === true,
      notes: (raw.notes || '').toString().trim(),
      customer_count: this.selectedRoute ? Number(this.selectedRoute.customer_count || 0) : 0
    };
  }
}
